<template>
	<v-list style="background: transparent;" flat>
		<SwipeList
			ref="swipelist"
			:items="items"
			item-key="guid"
			@active="onSwipeOutActive"
			@swipeout:click="onSwipeOutClick"
		>
			<template v-slot="{ item }">
				<!-- Room -->
				<v-list-item v-if="isRoom(item)" dense>
					<v-list-item-title class="subtitle-2" v-text="item.name" />
				</v-list-item>

				<!-- Device -->
				<template v-else>
					<v-list-item
						:class="{ 'device-list--child': item.isChild }"
						:disabled="disable && isPaired(item)"
					>
						<v-list-item-icon>
							<device-icon :device="item" :highlight="isPaired(item)" />
						</v-list-item-icon>

						<v-list-item-title>{{
							item.isChild ? humanizeNodeName(item) : item.name
						}}</v-list-item-title>
					</v-list-item>
				</template>
			</template>

			<template v-slot:left="{ item }">
				<!-- Child devices have no actions -->
				<template v-if="item && !item.isChild">
					<div
						@click="
							!isEmptyRoom(item)
								? $emit('update:showDeleteRoomSnackbar', true)
								: null
						"
					>
						<device-misc-swipe-action
							type="remove"
							:item="item"
							:disabled="!isEmptyRoom(item) || isController(item)"
							:small="isRoom(item)"
							@click="fnRemoveItem"
						/>
					</div>
				</template>
			</template>

			<template v-slot:right="{ item }">
				<!-- Child devices have no actions -->
				<template v-if="item && !item.isChild">
					<device-misc-swipe-action
						type="rename"
						:item="item"
						:small="isRoom(item)"
						@click="fnRenameItem"
					/>
					<device-misc-swipe-action
						v-if="!isRoom(item)"
						:disabled="isController(item)"
						type="move"
						:item="item"
						:small="isRoom(item)"
						@click="fnMoveItem"
					/>
				</template>
			</template>
		</SwipeList>
	</v-list>
</template>

<style lang="scss" scoped>
.device-list--child {
	padding-left: 40px;
}
</style>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import { SwipeList } from "vue-swipe-actions";
import "vue-swipe-actions/dist/vue-swipe-actions.css";

import {
	humanizeNodeName,
	DEVICE_CLASS_ROOM,
	DEVICE_CLASS_CONTROLLER,
} from "@/utils/k4util";

export default {
	components: { SwipeList },

	props: {
		nodes: { type: Array, default: () => [] },
		showNested: { type: Boolean, default: false },

		fnRemoveItem: { type: Function, required: true },
		fnRenameItem: { type: Function, required: true },
		fnMoveItem: { type: Function, required: true },

		fnClickItem: { type: Function, required: true },
		showDeleteRoomSnackbar: { type: Boolean, default: false },

		disable: Boolean,
	},

	computed: {
		...mapGetters({ nodeByGuid: "controller/nodes/nodeByGuid" }),

		items() {
			return _.reduce(
				this.nodes,
				(accum, node) => {
					node.guid = node.guid || node.meta.guid;
					accum.push(node);
					if (this.showNested && !this.isRoom(node) && node.children.length) {
						const children = _.map(node.children, guid => {
							const child = this.nodeByGuid(guid);
							child.guid = child.guid || child.meta.guid;
							child.isChild = true;
							return child;
						});
						accum = accum.concat(children);
					}
					return accum;
				},
				[]
			);
		},
	},

	beforeDestroy() {
		document.removeEventListener("click", this.onDocumentClick);
	},

	mounted() {
		document.addEventListener("click", this.onDocumentClick);
	},

	methods: {
		humanizeNodeName,

		isRoom(item) {
			const cls = item.meta ? item.meta.cls : item.cls;
			return cls === DEVICE_CLASS_ROOM;
		},

		// temporary - we cant move or rename controllers because of issue with controller code
		isController(item) {
			const cls = item.meta ? item.meta.cls : item.cls;
			return cls === DEVICE_CLASS_CONTROLLER;
		},

		isEmptyRoom(item) {
			return !item.children.length;
		},

		isPaired(item) {
			return item.configured || item.isChild;
		},

		onDocumentClick(event) {
			const swipeListEl = this.$refs.swipelist.$el;
			const target = event.target;
			if (target.nodeName === "HTML" || swipeListEl.contains(event.target)) {
				return;
			}

			this.$refs.swipelist.closeActions();
		},

		onSwipeOutActive(event) {
			this.$refs.swipelist.closeActions();
		},

		onSwipeOutClick(item) {
			if (this.isRoom(item)) {
				item = undefined;
			}
			this.fnClickItem(item);
		},
	},
};
</script>
